import React, { useState } from 'react';
import DmCheatSheet from '../dm/DmCheatSheet';

// ── Exploration / Social action sections ───────────────────────────────────
const ACTION_SECTIONS = [
  {
    title: 'Skill Checks',
    rows: [
      ['Athletics (STR)', 'Climb, jump, swim, grapple'],
      ['Acrobatics (DEX)', 'Balance, tumble, escape grapple'],
      ['Sleight of Hand (DEX)', 'Pickpocket, palm, plant'],
      ['Stealth (DEX)', 'Hide, move silently'],
      ['Arcana (INT)', 'Magic lore, identify spell or item'],
      ['History (INT)', 'Past events, legends, lore'],
      ['Investigation (INT)', 'Search clues, deduce, examine'],
      ['Nature (INT)', 'Terrain, weather, plants, animals'],
      ['Religion (INT)', 'Gods, holy symbols, rites'],
      ['Animal Handling (WIS)', 'Calm, ride, command beast'],
      ['Insight (WIS)', 'Read motives, detect lies'],
      ['Medicine (WIS)', 'Stabilize dying, diagnose illness'],
      ['Perception (WIS)', 'Spot hidden, hear, notice details'],
      ['Survival (WIS)', 'Track, forage, navigate wilderness'],
      ['Deception (CHA)', 'Lie convincingly'],
      ['Intimidation (CHA)', 'Threaten, coerce'],
      ['Performance (CHA)', 'Entertain, inspire crowd'],
      ['Persuasion (CHA)', 'Convince honestly, negotiate'],
    ],
  },
  {
    title: 'Typical DCs',
    rows: [
      ['Very Easy', 'DC 5 — trivial, most succeed'],
      ['Easy', 'DC 10'],
      ['Medium', 'DC 15'],
      ['Hard', 'DC 20'],
      ['Very Hard', 'DC 25'],
      ['Nearly Impossible', 'DC 30'],
    ],
  },
  {
    title: 'Passive Checks',
    rows: [
      ['Formula', 'Passive = 10 + modifier'],
      ['Advantage', '+5 to passive score'],
      ['Disadvantage', '-5 to passive score'],
      ['Use Case', 'Perception/Investigation without rolling; DM compares to DCs'],
    ],
  },
  {
    title: 'Exploration',
    rows: [
      ['Search', 'Investigation (details) or Perception (spot)'],
      ['Track', 'Survival — DC depends on ground, weather, time'],
      ['Climb', 'Athletics or half-speed without check if easy'],
      ['Swim', 'Athletics — difficult terrain in water'],
      ['Forage', 'Survival — DC 10 (plentiful) to 20 (scarce)'],
      ['Navigate', 'Survival — DC 10-15 to avoid getting lost'],
      ['Listen at Door', 'Perception'],
      ['Pick Lock', 'Sleight of Hand + thieves\u2019 tools'],
      ['Find Trap', 'Investigation or Perception'],
      ['Disarm Trap', 'Sleight of Hand + thieves\u2019 tools'],
      ['Break Object', 'Athletics vs object AC/HP'],
    ],
  },
  {
    title: 'Social Interaction',
    rows: [
      ['Parley', 'Persuasion, Intimidation, or Deception depending on approach'],
      ['Read Emotions', 'Insight — detect mood or lies'],
      ['Gather Info', 'Investigation or Persuasion in town, ~1 hr, DC 10-20'],
      ['Haggle', 'Persuasion for 10-20% discount'],
      ['Command Beast', 'Animal Handling to ride or direct'],
      ['Calm Hostile', 'Persuasion or Animal Handling'],
    ],
  },
  {
    title: 'Rest',
    rows: [
      ['Short Rest', '1 hour — spend Hit Dice to heal (HD + CON per die)'],
      ['Long Rest', '8 hours — full HP, half Hit Dice back, spell slots back'],
      ['Rule', 'Only one long rest per 24 hours'],
      ['Interrupted', 'If combat or 1+ hr walking, rest yields no benefit'],
    ],
  },
  {
    title: 'Downtime',
    rows: [
      ['Crafting', 'Days of work; materials cost 50% of item price'],
      ['Training', 'Weeks + gold to learn a new tool/language/skill'],
      ['Work', 'Earn 1 gp/day unskilled, 2 gp/day skilled'],
      ['Research', 'Investigation + library access; several days'],
      ['Recuperate', '3 days of rest to end exhaustion or disease (DC 15 CON)'],
    ],
  },
];

function SectionHeader({ title, open, onToggle }) {
  return (
    <button style={styles.sectionBtn} onClick={onToggle}>
      <span>{title}</span>
      <span style={styles.chevron}>{open ? '\u25B2' : '\u25BC'}</span>
    </button>
  );
}

function ActionsTab() {
  const [openSections, setOpenSections] = useState(() => {
    const init = {};
    ACTION_SECTIONS.forEach((s, i) => { init[s.title] = i < 2; });
    return init;
  });
  const toggle = (title) => setOpenSections((p) => ({ ...p, [title]: !p[title] }));
  const expandAll = () => {
    const all = {};
    ACTION_SECTIONS.forEach((s) => { all[s.title] = true; });
    setOpenSections(all);
  };

  return (
    <>
      <div style={styles.header}>
        <span style={styles.headerTitle}>Exploration &amp; Social</span>
        <button style={styles.expandBtn} onClick={expandAll}>Expand All</button>
      </div>
      <div style={styles.scroll}>
        {ACTION_SECTIONS.map((s) => (
          <div key={s.title} style={styles.section}>
            <SectionHeader title={s.title} open={openSections[s.title]} onToggle={() => toggle(s.title)} />
            {openSections[s.title] && (
              <div style={styles.table}>
                {s.rows.map(([k, v]) => (
                  <div key={k} style={styles.row}>
                    <span style={styles.rowKey}>{k}</span>
                    <span style={styles.rowVal}>{v}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

const TABS = ['Actions', 'Rules'];

export default function ReferencePanel() {
  const [activeTab, setActiveTab] = useState('Actions');
  return (
    <div style={styles.panel}>
      <div style={styles.tabBar}>
        {TABS.map((t) => (
          <button
            key={t}
            style={{ ...styles.tab, ...(activeTab === t ? styles.activeTab : {}) }}
            onClick={() => setActiveTab(t)}
          >
            {t}
          </button>
        ))}
      </div>
      <div style={styles.content}>
        {activeTab === 'Actions' && <ActionsTab />}
        {activeTab === 'Rules' && <DmCheatSheet />}
      </div>
    </div>
  );
}

const styles = {
  panel:       { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
  tabBar:      { display: 'flex', borderBottom: '2px solid #8b7340', flexShrink: 0 },
  tab:         { width: '50%', background: 'transparent', border: 'none', borderRadius: 0, padding: '10px 0', fontSize: 12, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", color: '#9a8b6e', cursor: 'pointer', letterSpacing: '0.04em', boxSizing: 'border-box' },
  activeTab:   { color: '#d4a847', borderBottom: '2px solid #d4a847' },
  content:     { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' },

  header:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #4a3d28', flexShrink: 0 },
  headerTitle: { fontFamily: "'Press Start 2P', monospace", fontSize: 13, fontWeight: 800, color: '#d4a847', textTransform: 'uppercase', letterSpacing: '0.07em' },
  expandBtn:   { background: 'transparent', border: '1px solid #4a3d28', borderRadius: 4, padding: '3px 10px', color: '#9a8b6e', fontSize: 11, cursor: 'pointer' },
  scroll:      { flex: 1, overflowY: 'auto' },

  section:     { borderBottom: '1px solid #2a2040' },
  sectionBtn:  { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'transparent', border: 'none', color: '#c0b090', fontFamily: "'Press Start 2P', monospace", fontSize: 13, fontWeight: 700, textAlign: 'left', cursor: 'pointer' },
  chevron:     { color: '#6a5d45', fontSize: 10 },
  table:       { padding: '0 14px 10px' },
  row:         { display: 'flex', gap: 10, padding: '5px 0', borderBottom: '1px solid #1a1a30' },
  rowKey:      { flexShrink: 0, width: 130, fontSize: 12, fontWeight: 700, color: '#d4a847' },
  rowVal:      { flex: 1, fontSize: 12, color: '#c0b090', lineHeight: 1.4 },
};
